import React, { useState } from 'react';
import { Play } from 'lucide-react';

interface SimonGameProps {
  onComplete: (score: number) => void;
}

const COLORS = [
    '#ef4444', '#f97316', '#facc15', 
    '#84cc16', '#22c55e', '#10b981',
    '#06b6d4', '#3b82f6', '#8b5cf6'
];

export default function SimonGame({ onComplete }: SimonGameProps) {
  const [sequence, setSequence] = useState<number[]>([]);
  const [playerSequence, setPlayerSequence] = useState<number[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [flash, setFlash] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState('');

  const startGame = () => {
    setSequence([]);
    setPlayerSequence([]);
    setScore(0);
    setMessage('');
    setIsPlaying(true);
    addToSequence([]);
  };

  const addToSequence = (currentSeq: number[]) => {
    setMessage('שים לב...');
    setTimeout(() => {
        const nextColorIndex = Math.floor(Math.random() * COLORS.length);
        const newSeq = [...currentSeq, nextColorIndex];
        setSequence(newSeq);
        setPlayerSequence([]);
        playSequence(newSeq);
    }, 1000);
  };

  const playSequence = (seq: number[]) => {
    let i = 0;
    const interval = setInterval(() => {
      setFlash(seq[i]);
      setTimeout(() => setFlash(null), 300); // Faster flash
      i++;
      if (i >= seq.length) {
        clearInterval(interval);
        setMessage('תורך!');
      }
    }, 600);
  };

  const handleColorClick = (index: number) => {
    if (flash !== null || !isPlaying || playerSequence.length >= sequence.length) return;
    
    // Flash effect
    setFlash(index);
    setTimeout(() => setFlash(null), 200);

    const newPlayerSeq = [...playerSequence, index];
    setPlayerSequence(newPlayerSeq);

    // Check if correct so far
    if (newPlayerSeq[newPlayerSeq.length - 1] !== sequence[newPlayerSeq.length - 1]) {
      // Wrong move
      setMessage('טעות! המשחק נגמר');
      setIsPlaying(false);
      onComplete(score); // Final score
      return;
    }

    // Level complete?
    if (newPlayerSeq.length === sequence.length) {
      const newScore = score + 3; // 3 points per stage as requested
      setScore(newScore);
      setMessage('מעולה!');
      setTimeout(() => addToSequence(sequence), 1000);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 h-full">
       <div className="flex justify-between w-full max-w-sm mb-4 text-foreground items-end">
         <h2 className="text-2xl font-bold">סיימון</h2>
         <div className="text-xl">ניקוד: {score}</div>
       </div>
       
       <div className="text-yellow-400 h-8 font-bold mb-4">{message}</div>

       <div className="grid grid-cols-3 gap-3 mb-8">
         {COLORS.map((color, i) => (
            <button
                key={i}
                onClick={() => handleColorClick(i)}
                className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl transition-all duration-100 border-b-4 border-black/20 active:scale-95 active:border-b-0 ${
                    flash === i ? 'brightness-150 scale-95 border-b-0 shadow-[0_0_30px_rgba(255,255,255,0.5)]' : 'brightness-100 shadow-lg'
                }`}
                style={{ backgroundColor: color }}
            />
         ))}
       </div>

       {!isPlaying && (
         <button onClick={startGame} className="bg-white text-foreground px-8 py-3 rounded-full font-bold flex items-center gap-2 hover:scale-105 transition-transform shadow-xl">
            <Play size={20} /> התחל משחק חדש
         </button>
       )}
    </div>
  );
}
